import Floating from "@/comps/floating"
import { useClientAuth } from "@/utils/client-auth"

import {
	DesktopOrTablet,
	devices,
	Mobile,
	onDesktop,
	onTablet,
} from "@/utils/breakpoints"
import clsx from "clsx"
import { useTranslation } from "next-i18next"
import { useRouter } from "next/router"

import { useAdjustLogger } from "@/utils/adjust"
import { useCallback, useEffect, useState } from "react"
import { useQuery } from "react-query"
import { Box, FlexBox } from "react-styled-flex"
import styled, { css } from "styled-components"
import {
	aiAdsGenratorIcon,
	aiScriptGenerator,
	productUrlToAdsIcon,
	scriptToAdsGenerator,
} from "../ai-tools/icons"
import { DropDown } from "../dropdown"
import {
	fetchOfferingPrices,
	getCurrentPackId,
	SubscriptionPopup,
} from "../subscription-popup"
import { AvatarWithMenu } from "./avatar-with-menu"
import {
	HamburgerAnimated,
	Logo,
	LogoSmallScaled,
	ThemeToggle,
} from "./icon"
import Menu from "./menu"
import HeaderTab from "./tab"

const HeaderPhony = styled.div`
	height: 70px;
	background-color: #ecedef00;
`

const HeaderBackground = styled.div<{
	outside: boolean
	solid?: boolean
	transparent?: boolean
}>`
	.light && {
		${(props) =>
			props.solid
				? css`
						background-color: rgba(253, 253, 254, 1);
				  `
				: css`
						background-color: rgba(255, 255, 255, 0.8);
				  `};
	}

	.dark && {
		${(props) =>
			props.solid
				? css`
						background-color: rgba(28, 28, 28, 1);
				  `
				: css`
						background-color: rgba(18, 18, 18, 0.7);
				  `};
	}

	${(props) =>
		props.transparent
			? css`
					background-color: transparent !important;
			  `
			: css`
					backdrop-filter: blur(10px);
					border-bottom: 1px solid var(--color-separator);
			  `}

	width: var(--w-screen);
	height: 70px;

	display: flex;
	align-items: center;
	justify-content: center;

	overflow: visible;

	position: fixed;
	top: ${(props) => (props.outside ? -90 : 0)}px;
	left: 0;
	z-index: 99;

	transition: top 300ms;
	transition-timing-function: ease-in-out;
`

const HeaderContainer = styled.div`
	height: 70px;

	display: flex;

	${onTablet(css`
		justify-content: flex-start;
		align-items: center;
		padding: 0 16px;
	`)}

	${onDesktop(css`
		width: 1392px;
		padding: 0 24px;
	`)}
`

const Stretchable = styled.div`
	display: none;

	@media ${devices.desktop} {
		flex: 1;
		display: inline;
	}
`

const MobileHeader = styled.div`
	position: fixed;
	z-index: 99;
	top: 0;
	left: 0;
	${onTablet(css`
		display: none;
	`)}
`

const TabUnderline = styled.div`
	position: absolute;
	z-index: 49;
	bottom: -24px;
	left: 50%;
	right: 50%;

	height: 2px;
	border-radius: 2px;
	background-color: var(--color-primary-500);

	transition: left 300ms ease-in-out,
		right 300ms ease-in-out;
`

const HeaderTabWrapper = styled.div`
	position: relative;

	&&:hover ${TabUnderline} {
		left: 12px;
		right: 12px;
	}
`

type HeaderItemType = {
	icon: JSX.Element
	name: string
	href: string
	desc?: string
	isNew?: boolean
}

const videoGptContent: HeaderItemType[] = [
	{
		icon: aiAdsGenratorIcon(),
		name: "txt_ai_ads_generator",
		desc: "txt_ai_ads_generator_desc",
		href: "/tools/ai-ad-generator",
	},
	{
		icon: productUrlToAdsIcon(),
		name: "txt_product_url_to_ads",
		desc: "txt_product_url_to_ads_desc",
		href: "/tools/url-to-video-maker",
	},
	{
		icon: aiScriptGenerator(),
		name: "txt_script_generator",
		desc: "txt_script_generator_desc",
		href: "/tools/ai-script-generator",
	},
	{
		icon: scriptToAdsGenerator(),
		name: "txt_script_to_ads_generator",
		desc: "txt_script_to_ads_generator_desc",
		href: "/tools/ai-script-to-video-generator",
	},
]

interface HeaderProps {
	isStatic?: boolean
	isSolid?: boolean
	isTransparent?: boolean
	hideHiring?: boolean
}

const Header = (props: HeaderProps) => {
	const { isStatic, isSolid, isTransparent, hideHiring } =
		props

	const [show, setShow] = useState(false)
	const [scroll, setScroll] = useState(0)
	const [outside, setOutside] = useState(false)
	const [_, setShowNew] = useState(false)

	const [subscriptionPopupOpen, setSubscriptionPopupOpen] =
		useState(false)

	const { userInfo } = useClientAuth()
	const logAdjust = useAdjustLogger()

	const [active, setActive] = useState("")

	const startHiding = 200

	const handleScroll = useCallback(() => {
		const current = window.scrollY

		if (current < startHiding) {
			if (outside) setOutside(false)
			return
		}

		if (scroll >= current) {
			setOutside(false)
		} else {
			setOutside(true)
		}

		setScroll(window.scrollY)
	}, [scroll, outside])

	const router = useRouter()
	useEffect(() => {
		setActive(router.route)
	}, [router])

	useEffect(() => {
		document.addEventListener("scroll", handleScroll)
		return () =>
			document.removeEventListener("scroll", handleScroll)
	}, [handleScroll])

	const { t } = useTranslation()

	useEffect(() => {
		if (typeof window !== "undefined" && userInfo) {
			const visited =
				localStorage.getItem(
					`visited-video-gpt-${userInfo.userId}`,
				) !== null
			setShowNew(!visited)
		}
	}, [userInfo])

	// const handleClick = () => {
	// 	if (userInfo) {
	// 		localStorage.setItem(
	// 			`visited-video-gpt-${userInfo.userId}`,
	// 			"true",
	// 		)
	// 		setShowNew(false)
	// 	}
	// }

	return (
		<>
			<DesktopOrTablet>
				{!isTransparent && <HeaderPhony />}
				<HeaderBackground
					outside={!isStatic && outside}
					solid={isSolid}
					transparent={isTransparent}>
					<HeaderContainer>
						<Logo hideHiring={hideHiring} />
						<div className="hidden flex-row items-center gap-[18px] tablet:flex desktop:gap-6">
							<HeaderTabWrapper>
								<DropDown
									hover
									className="group"
									trigger={
										<div
											className={clsx(
												"flex items-center gap-1 pl-2 font-500 text-blue-700 transition-colors duration-300 ease-in-out group-hover:text-primary-500",
											)}>
											{t("lbl_ai_tools")}

											<div
												className={clsx(
													"transition-all [--icon-color:var(--color-blue-700)]",
													"group-hover:-rotate-180 group-hover:[--icon-color:var(--color-primary-500)]",
												)}>
												<svg
													width="14"
													height="14"
													viewBox="0 0 16 16"
													fill="none"
													xmlns="http://www.w3.org/2000/svg">
													<path
														d="M12 6L8 10L4 6"
														stroke="var(--icon-color)"
														strokeWidth="1.33333"
														strokeLinecap="round"
														strokeLinejoin="round"
													/>
												</svg>
											</div>
										</div>
									}>
									<div className="absolute z-50 pt-3">
										<div
											className={clsx(
												"rounded-[10px] bg-color-popup-cell p-[10px]",
												"[box-shadow:0px_0px_30px_rgba(0,0,0,0.13)]",
												"flex w-[395px] flex-col font-500",
											)}>
											{videoGptContent.map(
												(item, index) => {
													return (
														<a
															key={index}
															href={item.href}
															className={clsx(
																"flex items-start gap-3 self-stretch rounded-[8px]",
																"px-[10px] py-[15px] transition-colors [--icon-color:var(--color-blue-600)]",
																"hover:bg-color-surface",
															)}>
															{item.icon}
															<div className="flex flex-col items-start gap-[1px]">
																<p
																	className={clsx(
																		"text-[16px] font-500 leading-[19px] text-blue-800",
																	)}>
																	{t(item.name)}
																</p>
																{item.desc && (
																	<p
																		className={clsx(
																			"w-[239px] text-[13px] font-400 leading-[19px] text-blue-600",
																		)}>
																		{t(item.desc)}
																	</p>
																)}
															</div>
														</a>
													)
												},
											)}
										</div>
									</div>
								</DropDown>
							</HeaderTabWrapper>
							<HeaderTabWrapper>
								<HeaderTab
									link="/ad-examples"
									name="lbl_ad_examples"
									active={"/ad-examples" === active}
								/>
							</HeaderTabWrapper>
							<HeaderTabWrapper className="hidden desktop:block">
								<HeaderTab
									link="/pricing"
									name="lbl_pricing"
									active={"/pricing" === active}
								/>
								<TabUnderline />
							</HeaderTabWrapper>
						</div>
						<Stretchable />
						<div className="flex min-w-[200px] justify-end gap-4">
							{!userInfo ? (
								<AvatarWithMenu
									setSubscriptionPopupOpen={
										setSubscriptionPopupOpen
									}
								/>
							) : (
								<a
									href="/home"
									onClick={() =>
										logAdjust?.logEventWithPayload(
											"w_dp_dashboard",
											{ from: router.asPath },
										)
									}
									className={clsx(
										"bg-primary-500 px-4 py-[6px] text-[16px] font-600 hover:bg-primary-600",
										"rounded-[6px] text-color-white",
										"animate-appear transition-colors",
									)}>
									{t("lbl_dashboard")}
								</a>
							)}
							{!userInfo && <ThemeToggle />}
						</div>
					</HeaderContainer>
				</HeaderBackground>
			</DesktopOrTablet>
			<Mobile>
				<Box height={48} />
				<MobileHeader className="bg-[#ecedef] dark:bg-[#121212]">
					<FlexBox
						center
						justifyContent="space-between"
						width="var(--w-screen)"
						padding="4px 14px">
						<LogoSmallScaled />
						<div className="flex items-center gap-4">
							{!userInfo ? (
								<AvatarWithMenu
									setSubscriptionPopupOpen={
										setSubscriptionPopupOpen
									}
									onFocus={() => setShow(false)}
								/>
							) : (
								<a
									href="/home"
									onClick={() =>
										logAdjust?.logEventWithPayload(
											"w_dp_dashboard",
											{ from: router.asPath },
										)
									}
									className={clsx(
										"bg-primary-500 px-4 py-[6px] text-[16px] font-600 hover:bg-primary-600",
										"rounded-[6px] text-color-white",
										"animate-appear transition-colors",
									)}>
									{t("lbl_dashboard")}
								</a>
							)}
							<HamburgerAnimated
								onClick={() => setShow(!show)}
								show={show}
							/>
						</div>
					</FlexBox>
					<Floating position={{ top: 8, left: 0 }}>
						<FlexBox center></FlexBox>
					</Floating>
				</MobileHeader>
				<Menu show={show} hide={() => setShow(false)} />
			</Mobile>

			<SubscriptionPopup
				isOpen={subscriptionPopupOpen}
				close={() => setSubscriptionPopupOpen(false)}
				location={router.asPath}
			/>
		</>
	)
}

interface UpgradeButtonProps {
	setSubscriptionPopupOpen: (v: boolean) => void
}

export function UpgradeButton(props: UpgradeButtonProps) {
	const { setSubscriptionPopupOpen } = props
	const { t } = useTranslation()
	const { userInfo } = useClientAuth()
	const logAdjust = useAdjustLogger()
	const router = useRouter()

	const prices = useQuery({
		queryKey: ["offering-prices"],
		queryFn: fetchOfferingPrices(),
		staleTime: Infinity,
		cacheTime: Infinity,
	})

	const isFreeUser =
		userInfo &&
		prices.data &&
		getCurrentPackId(userInfo.entitlements, prices.data)
			.packId === "free"

	return (
		<div
			className={clsx(
				isFreeUser ? "flex" : "hidden",
				"items-center",
			)}>
			<button
				onClick={(e) => {
					e.preventDefault()
					logAdjust?.logEventWithPayload("w_dp_upgrade", {
						from: router.asPath,
					})
					setSubscriptionPopupOpen(true)
				}}
				className={clsx(
					"flex h-8 w-[92px] items-center justify-center rounded-[8px]",
					"bg-green-100 text-[16px] font-600 text-green-800",
					"transition-colors duration-300 hover:bg-green-200",
					"tablet:h-9 tablet:w-[103px]",
				)}>
				{t("txt_upgrade")}
			</button>
		</div>
	)
}

export function New() {
	const { t } = useTranslation()

	return (
		<div className="ml-1 flex h-5 w-[37px] items-center justify-center rounded bg-primary-100 text-center text-[10px] font-700 text-primary-500">
			{t("lbl_new")}
		</div>
	)
}

export default Header
